import {UseCase} from '@s-ui/domain'

class LoginAuthenticationUseCase extends UseCase {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Config} deps.config
   * @param {LoginAuthenticationService} deps.service
   * @param {SessionStorageAuthenticationRepository} deps.fallbackRepository
   * @param {configRepository} deps.defaultRepository
   * @param {Function<GigyaSignatureEntity>} deps.gigyaSignatureEntity
   * @param {LoginInfoAuthenticationValueObject} deps.valueObject
   * @param {GetIdentifyTrackingDataService} deps.getIdentifyTrackingDataService
   */
  constructor({
    config,
    loginAuthenticationService,
    fallbackRepository,
    defaultRepository,
    gigyaSignatureEntity,
    loginInfoAuthenticationValueObject,
    getIdentifyTrackingDataService
  }) {
    super()

    this._config = config
    this._loginAuthenticationService = loginAuthenticationService
    this._fallbackRepository = fallbackRepository
    this._defaultRepository = defaultRepository
    this._gigyaSignatureEntity = gigyaSignatureEntity
    this._loginInfoAuthenticationValueObject = loginInfoAuthenticationValueObject
    this._getIdentifyTrackingDataService = getIdentifyTrackingDataService
  }

  _checkPathFormat = path => {
    return path.startsWith('/')
  }

  _getUrl = path =>
    new Promise((resolve, reject) =>
      this._checkPathFormat(path) ? resolve(path) : reject(Error('LoginAuthenticationUseCase _getUrl error'))
    )

  _getUrlToRedirect = ({path, userData}) => {
    return userData.getContractId()
      ? Promise.resolve(this._config.get('user').PROFESSIONAL_SELLER_LOGIN_URL)
      : this._getUrl(path)
          .catch(() => this._fallbackRepository.getUrl())
          .catch(() => this._defaultRepository.getUrl())
  }

  _getLoginProvider({gigyaResponse}) {
    const {provider} = gigyaResponse

    const {GIGYA_LOGIN_PROVIDERS} = this._config.get('login')

    const [loginProvider] =
      Object.entries(GIGYA_LOGIN_PROVIDERS).find(([_, providerKey]) => providerKey === provider) || []

    return loginProvider || ''
  }

  /**
   * @param {Object} params
   * @param {Object} params.gigyaResponse Data from the gigya onLogin event
   * @param {Object} params.path targetPage query param value
   * @return {Promise<void>} The returned promise has failed or is correct without information.
   */
  async execute({gigyaResponse, path, suppressVerificationEmail}) {
    const gigyaSignatureEntity = this._gigyaSignatureEntity(gigyaResponse)
    const userData = await this._loginAuthenticationService.execute({
      gigyaSignatureEntity,
      suppressVerificationEmail
    })

    const url = await this._getUrlToRedirect({path, userData})
    const {newUser} = gigyaResponse

    const loginProvider = this._getLoginProvider({gigyaResponse})

    // Resolved here (the use case is already async) so subscribers can forward `identify` to Segment
    // synchronously — pages/Login redirects from its own subscriber, so the UI can't await anything.
    const {TRACKING_EVENT_NAMES} = this._config.get('tracking')
    const identify = await this._getIdentifyTrackingDataService.execute({
      event: newUser ? TRACKING_EVENT_NAMES.SIGNED_UP : TRACKING_EVENT_NAMES.SIGNED_IN
    })

    return this._loginInfoAuthenticationValueObject({
      isNewUser: newUser,
      loginProvider,
      url,
      userData: userData.toJSON(),
      ...(identify && {identify})
    }).toJSON()
  }
}
export default LoginAuthenticationUseCase
